import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { IoLockClosed } from 'react-icons/io5';
import CText from './CText';
import { SubscriptionTier } from '../../store/slices/userSlice';

interface SubscriptionGateProps {
  allowed: boolean;
  requiredTier: SubscriptionTier;
  reason?: string;
  /** 'inline' shows a small card, 'overlay' covers the full area */
  mode?: 'inline' | 'overlay';
  children: React.ReactNode;
}

const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
};

const SubscriptionGate: React.FC<SubscriptionGateProps> = ({
  allowed,
  requiredTier,
  reason,
  mode = 'inline',
  children,
}) => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  if (allowed) {
    return <>{children}</>;
  }

  const label = TIER_LABELS[requiredTier] || 'Pro';
  const displayReason = reason || `Requires ${label} plan`;

  if (mode === 'overlay') {
    return (
      <div className="relative min-h-[60vh] flex items-center justify-center">
        {/* Blurred background placeholder */}
        <div className="absolute inset-0 opacity-20 blur-sm pointer-events-none">
          {children}
        </div>
        {/* Gate card */}
        <div
          className="relative z-10 flex flex-col items-center gap-4 p-8 rounded-2xl max-w-sm mx-auto text-center"
          style={{
            backgroundColor: colors.inputBackground,
            border: `1px solid ${colors.borderColor}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.primary + '20' }}
          >
            {React.createElement(IoLockClosed as any, { size: 28, color: colors.primary })}
          </div>
          <CText type="B18" color={colors.textColor}>
            {label} Feature
          </CText>
          <CText type="r14" color={colors.grayScale500}>
            {displayReason}
          </CText>
          <button
            onClick={() => navigate('/pricing')}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors"
            style={{ backgroundColor: colors.primary, color: colors.white }}
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  // Inline mode — small card
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl"
      style={{
        backgroundColor: colors.inputBackground,
        border: `1px solid ${colors.borderColor}`,
      }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: colors.primary + '20' }}
      >
        {React.createElement(IoLockClosed as any, { size: 14, color: colors.primary })}
      </div>
      <div className="flex-1 min-w-0">
        <CText type="M12" color={colors.textColor}>
          {label} Only
        </CText>
        <CText type="r12" color={colors.grayScale500}>
          {displayReason}
        </CText>
      </div>
      <button
        onClick={() => navigate('/pricing')}
        className="px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0"
        style={{ backgroundColor: colors.primary, color: colors.white }}
      >
        Upgrade
      </button>
    </div>
  );
};

export default SubscriptionGate;
